import { calculateDirectorySize } from "../calculateDirectorySize";
import { Magazine, pageFiles } from "../Datatypes";
import { fetchSinglePage } from "../FetchFromAPI";
import * as FileSystem from 'expo-file-system';
import { usePersistBoundStore } from "../zustand/Store";

type DownloadTask = {
    magazine: Magazine;
    page: number;
    controller: AbortController;
    status: 'pending' | 'downloading' | 'completed' | 'canceled' | 'failed';
    promise?: Promise<void>;
};

class DownloadManager {
    private tasks: Map<string, DownloadTask> = new Map();
    private queue: DownloadTask[] = [];
    private activeDownloads: Set<string> = new Set();
    private maxConcurrentDownloads = 3;

    private generateTaskKey(magazine: Magazine, page: number): string {
        return `${magazine.date}-${magazine.edition}-${page}`;
    }

    addDownload(magazine: Magazine): void {
        if (!magazine.pageFiles) return
        for (let page = 1; page <= magazine.pageFiles.length; page++) {
            const taskKey = this.generateTaskKey(magazine, page);

            if (this.tasks.has(taskKey)) {
                //console.log(`Download for page ${page} of magazine ${magazine.edition} is already scheduled.`);
                continue;
            }

            const controller = new AbortController();
            const task: DownloadTask = {
                magazine,
                page,
                controller,
                status: 'pending',
            };

            this.tasks.set(taskKey, task);
            this.queue.push(task);
        }

        this.processQueue();
    }

    addSingleDownload(magazine: Magazine, pageFile: pageFiles): void {
        if (!pageFile) return
            const taskKey = this.generateTaskKey(magazine, pageFile.page);

            const controller = new AbortController();
            const task: DownloadTask = {
                magazine,
                page: pageFile.page,
                controller,
                status: 'pending',
            };

            this.tasks.set(taskKey, task);
            this.queue.push(task);

        this.processQueue();
    }

    private async processQueue(): Promise<void> {
        while (this.activeDownloads.size < this.maxConcurrentDownloads && this.queue.length > 0) {
            const task = this.queue.shift();
            if (!task) continue;

            const taskKey = this.generateTaskKey(task.magazine, task.page);

            // Skip canceled tasks
            if (task.status === 'canceled') {
                //console.log(`Skipping canceled task for page ${task.page} of magazine ${task.magazine.edition}.`);
                this.tasks.delete(taskKey);
                continue;
            }

            this.activeDownloads.add(taskKey);

            task.promise = this.startDownload(task)
                .finally(() => {
                    this.activeDownloads.delete(taskKey);
                    this.tasks.delete(taskKey);
                    this.processQueue(); // Start the next task in the queue
                });
        }
    }

    orderQueue(magazine: Magazine, page?: number): void {
        if (page !== undefined) {
            // Handle single-page reordering
            const taskKey = this.generateTaskKey(magazine, page);
            const task = this.tasks.get(taskKey);

            if (task) {
                if (this.activeDownloads.has(taskKey)) {
                    // console.log(`Task for page ${page} of magazine ${magazine.edition} is currently downloading and cannot be reordered.`);
                } else {
                    this.pushToFirst(taskKey);
                }
            } else {
                //console.log(`Task for page ${page} of magazine ${magazine.edition} does not exist.`);
            }
        } else {
            // Handle reordering all pages of a magazine
            const magazineTasks = this.queue.filter(
                (queuedTask) => queuedTask.magazine.date === magazine.date && queuedTask.magazine.edition === magazine.edition
            );

            // Sort tasks by page number in ascending order
            magazineTasks.sort((a, b) => b.page - a.page);

            // Push all sorted tasks to the top of the queue
            for (const task of magazineTasks) {
                const taskKey = this.generateTaskKey(task.magazine, task.page);
                this.pushToFirst(taskKey);
            }

            //console.log(`All tasks for magazine ${magazine.edition} have been moved to the top of the queue, ordered by page number.`);
        }
    }

    pushToFirst(taskKey: string): void {
        const taskIndex = this.queue.findIndex(
            (queuedTask) => this.generateTaskKey(queuedTask.magazine, queuedTask.page) === taskKey
        );

        if (taskIndex !== -1) {
            // Remove the task from the queue
            const [task] = this.queue.splice(taskIndex, 1);

            // Add it to the beginning of the queue
            this.queue.unshift(task);

            //console.log(`Task for ${taskKey} has been moved to the first position in the queue.`);
        } else {
            //console.log(`Task for ${taskKey} is not in the queue.`);
        }
    }

    private async startDownload(task: DownloadTask): Promise<void> {
        const { magazine, page, controller, status } = task;
        const getStoredMagazineByMagazine = usePersistBoundStore.getState().getStoredMagazineByMagazine;
        const updatePageFile = usePersistBoundStore.getState().updatePageFile;

        const stored = getStoredMagazineByMagazine(magazine);
        if (!stored) return;
        if (!stored.pageFiles) return;

        const storedPageFile = stored.pageFiles[page - 1];
        if (storedPageFile.downloaded != false) return;



        task.status = 'downloading';
        try {
            //console.log(`Starting download for page ${page} of magazine ${magazine.edition}...`);
            const result = await fetchSinglePage(magazine.date, magazine.edition, page.toString(), {
                signal: controller.signal,
            });

            if (result) {

                const size = await calculateDirectorySize(`${FileSystem.documentDirectory}/epaper/${magazine.date}/${magazine.edition}/${page}`);
                updatePageFile(stored, storedPageFile.page, new Date(), size);

                task.status = 'completed';
            } else {
                console.error(`Failed to download page ${page} of magazine ${magazine.edition}.`);
                task.status = 'failed';
            }
        } catch (error) {
            if (controller.signal.aborted) {
                //console.log(`Download for page ${page} of magazine ${magazine.edition} was canceled.`);
                task.status = 'canceled';
            } else {
                console.error(`Error downloading page ${page}:`, error);
                task.status = 'failed';
            }
        }
    }

    cancelDownload(magazine: Magazine, page: number): void {
        const taskKey = this.generateTaskKey(magazine, page);

        const task = this.tasks.get(taskKey);
        if (task) {
            if (this.activeDownloads.has(taskKey)) {
                task.controller.abort();
                task.status = 'canceled';
                this.activeDownloads.delete(taskKey);
            } else {
                this.queue = this.queue.filter((queuedTask) => this.generateTaskKey(queuedTask.magazine, queuedTask.page) !== taskKey);
            }

            this.tasks.delete(taskKey);
            //console.log(`Download for page ${page} of magazine ${magazine.edition} was canceled.`);
        } else {
            //console.log(`No active download for page ${page} of magazine ${magazine.edition} to cancel.`);
        }
    }

    cancelDownloadByMagazine(magazine: Magazine): void {
        if (!magazine.pageFiles || magazine.pageFiles.length === 0) return;

        for (let page = 1; page <= magazine.pageFiles.length; page++) {
            const taskKey = this.generateTaskKey(magazine, page);
            const task = this.tasks.get(taskKey);

            if (task) {
                if (this.activeDownloads.has(taskKey)) {
                    // Cancel active downloads
                    task.controller.abort();
                    task.status = 'canceled';
                    this.activeDownloads.delete(taskKey);
                    //console.log(`Active download for page ${page} of magazine ${magazine.edition} was canceled.`);
                } else {
                    // Remove queued tasks
                    this.queue = this.queue.filter(
                        (queuedTask) => this.generateTaskKey(queuedTask.magazine, queuedTask.page) !== taskKey
                    );
                    //console.log(`Queued download for page ${page} of magazine ${magazine.edition} was removed.`);
                }

                // Remove the task from the main tasks map
                this.tasks.delete(taskKey);
            } else {
                //console.log(`No active or queued download for page ${page} of magazine ${magazine.edition}.`);
            }
        }
    }

    cancelAll(): void {
        this.queue = [];
        this.tasks.forEach((task, taskKey) => {
            if (this.activeDownloads.has(taskKey)) {
                task.controller.abort();
                task.status = 'canceled';
                this.activeDownloads.delete(taskKey);
            }
        });

        this.tasks.clear();
        console.log('All downloads have been canceled.');
    }

    getStatus(): DownloadTask[] {
        return Array.from(this.tasks.values());
    }
}

export const downloadManager = new DownloadManager();
